import { DEMO_ORGS } from "../../mock/organisations";
import { LATERITE_PEOPLE, PEOPLE } from "../../mock/people";
import type { Person } from "../../types";
import { IDENTITY_POLICY, TIME } from "../policy";
import { DEMO_TOKENS } from "./demo-content";
import type { IdentityState, StoredInvitation, StoredUser } from "./state";

/**
 * First-run contents of the mock identity service: two organisations, their people, a few invitations in each
 * state, a firm waiting in the staff console and two project access requests. Times are relative to now.
 */

export interface SeedOptions {
  now: number;
  demoOrg: { name: string; projectPrefix: string };
  hash: (text: string) => Promise<string>;
}

const DAY = TIME.MS_PER_DAY;
const HOUR = 60 * TIME.MS_PER_MINUTE;
/** Seeded accounts were created and onboarded long ago. */
const ACCOUNT_AGE_DAYS = 400;

function seededUser(person: Person, now: number, extra: Partial<StoredUser> = {}): StoredUser {
  const created = now - ACCOUNT_AGE_DAYS * DAY;
  return {
    id: person.id,
    name: person.name,
    email: person.email,
    initials: person.initials,
    colour: person.colour,
    title: person.title,
    createdAt: created,
    termsVersion: IDENTITY_POLICY.termsVersion,
    termsAcceptedAt: created,
    ageConfirmedAt: created,
    workspaceChosen: true,
    timeZone: "Asia/Kolkata",
    units: "metric",
    passkeys: [],
    passkeyPromptDismissed: true,
    ...extra,
  };
}

function isoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export async function seedIdentity({ now, demoOrg, hash }: SeedOptions): Promise<IdentityState> {
  const users = [...PEOPLE, ...LATERITE_PEOPLE].map((person) => {
    if (person.id === "p-asha") {
      return seededUser(person, now, {
        passkeys: [
          {
            id: "pk-asha-macbook",
            label: "iCloud Keychain on MacBook Air",
            createdAt: now - 200 * DAY,
          },
        ],
        twoStep: "Authenticator app",
        coa: { number: "CA/2009/45127", validUntil: "2027-12-31" },
      });
    }
    if (person.id === "p-rohan") {
      return seededUser(person, now, {
        passkeys: [
          {
            id: "pk-rohan-pixel",
            label: "Google Password Manager on Pixel 8",
            createdAt: now - 90 * DAY,
          },
        ],
      });
    }
    return seededUser(person, now);
  });

  const northlightRoles: Record<string, "owner" | "admin" | "member"> = {
    "p-asha": "owner",
    "p-rohan": "admin",
  };
  const memberships = [
    ...PEOPLE.map((person) => ({
      userId: person.id,
      organisationId: DEMO_ORGS.northlight,
      role: northlightRoles[person.id] ?? ("member" as const),
      joinedAt: now - (ACCOUNT_AGE_DAYS - 10) * DAY,
    })),
    {
      userId: "p-farah",
      organisationId: DEMO_ORGS.laterite,
      role: "owner" as const,
      joinedAt: now - 300 * DAY,
    },
    {
      userId: "p-joseph",
      organisationId: DEMO_ORGS.laterite,
      role: "member" as const,
      joinedAt: now - 280 * DAY,
    },
    {
      userId: "p-asha",
      organisationId: DEMO_ORGS.laterite,
      role: "member" as const,
      joinedAt: now - 120 * DAY,
    },
  ];

  const invitation = async (
    id: string,
    token: string,
    email: string,
    role: StoredInvitation["role"],
    invitedBy: string,
    daysAgo: number,
    status: StoredInvitation["status"] = "pending",
  ): Promise<StoredInvitation> => {
    const createdAt = now - daysAgo * DAY;
    return {
      id,
      tokenHash: await hash(token),
      organisationId: DEMO_ORGS.northlight,
      email,
      role,
      invitedBy,
      createdAt,
      expiresAt: createdAt + IDENTITY_POLICY.invitationMs,
      lastSentAt: createdAt,
      status,
    };
  };

  const invitations = await Promise.all([
    invitation(
      "inv-tara",
      DEMO_TOKENS.memberInvitation,
      "tara.menon@example.in",
      "member",
      "Asha Rao",
      2,
    ),
    invitation(
      "inv-ishaan",
      DEMO_TOKENS.guestInvitation,
      "ishaan.guest@example.in",
      "guest",
      "Rohan Mehta",
      9,
    ),
    invitation(
      "inv-kiran",
      DEMO_TOKENS.expiredInvitation,
      "kiran.patel@example.in",
      "member",
      "Asha Rao",
      20,
    ),
    invitation(
      "inv-wrong",
      DEMO_TOKENS.revokedInvitation,
      "wrong.address@example.in",
      "member",
      "Rohan Mehta",
      3,
      "revoked",
    ),
  ]);

  const inviteEmail = (
    to: string,
    inviter: string,
    role: string,
    token: string,
    daysAgo: number,
  ) => ({
    id: `mail-${to}`,
    to,
    subject: `${inviter} invited you to ${demoOrg.name} on Orthos`,
    sentAt: now - daysAgo * DAY,
    body: [
      `${inviter} invited you to join ${demoOrg.name} on Orthos as ${role}.`,
      "Orthos is the design studio in a browser tab. This invitation lasts 14 days.",
    ],
    href: `/invite/${token}`,
    linkLabel: "Accept invitation",
  });

  return {
    users,
    organisations: [
      {
        id: DEMO_ORGS.northlight,
        workosId: "org_01JBN0RTHL1GHTSTUD10DEMO",
        name: demoOrg.name,
        projectPrefix: demoOrg.projectPrefix,
        country: "India",
        state: "Gujarat",
        city: "Ahmedabad",
        region: IDENTITY_POLICY.storageRegion.id,
        status: "active",
        allowances: { seats: 12, guests: 5, storageGb: 500 },
        storageUsedGb: 184,
        accessFrom: isoDate(now - 300 * DAY),
        accessUntil: isoDate(now + 430 * DAY),
        plan: "studio",
        setupComplete: true,
        twoStepRequired: false,
        createdAt: now - ACCOUNT_AGE_DAYS * DAY,
      },
      {
        id: DEMO_ORGS.laterite,
        workosId: "org_01JBLATER1TEC0LLECT1VE00",
        name: "Laterite Collective",
        projectPrefix: "LC",
        country: "India",
        state: "Kerala",
        city: "Kochi",
        region: IDENTITY_POLICY.storageRegion.id,
        status: "active",
        allowances: { seats: 10, guests: 5, storageGb: 200 },
        storageUsedGb: 37,
        accessFrom: isoDate(now - 300 * DAY),
        accessUntil: isoDate(now + 200 * DAY),
        plan: "studio",
        setupComplete: true,
        twoStepRequired: false,
        createdAt: now - 300 * DAY,
      },
    ],
    memberships,
    invitations,
    orgRequests: [
      {
        id: "req-kapoor-sen",
        name: "Rhea Kapoor",
        email: "rhea@kapoorsen.example",
        firm: "Kapoor & Sen Architects",
        city: "Pune",
        teamSize: "6-15",
        disciplines: ["A", "I"],
        message: "We would like to move our residential and interiors work onto one model.",
        status: "pending",
        createdAt: now - 26 * HOUR,
      },
    ],
    projectRequests: [
      {
        id: "preq-meera-gachibowli",
        organisationId: DEMO_ORGS.northlight,
        projectId: "gachibowli-tower",
        projectName: "Gachibowli Office Tower",
        userId: "p-meera",
        message: "I'm detailing the lobby interiors and need the current ground floor plan.",
        status: "pending",
        createdAt: now - 2 * DAY,
        expiresAt: now - 2 * DAY + IDENTITY_POLICY.projectRequestMs,
      },
      {
        id: "preq-arjun-gachibowli",
        organisationId: DEMO_ORGS.northlight,
        projectId: "gachibowli-tower",
        projectName: "Gachibowli Office Tower",
        userId: "p-arjun",
        message: "Starting the podium landscape. Could I see the site plan?",
        status: "pending",
        createdAt: now - 5 * HOUR,
        expiresAt: now - 5 * HOUR + IDENTITY_POLICY.projectRequestMs,
      },
    ],
    projectGrants: [],
    sessions: [
      {
        id: "sess-asha-iphone",
        userId: "p-asha",
        organisationId: DEMO_ORGS.northlight,
        createdAt: now - 3 * DAY,
        lastActiveAt: now - 2 * HOUR,
        authMethod: "passkey",
        device: "iPhone",
        browser: "Safari",
        city: "Ahmedabad",
        ip: "49.36.112.18",
      },
      {
        id: "sess-asha-office",
        userId: "p-asha",
        organisationId: DEMO_ORGS.northlight,
        createdAt: now - 12 * DAY,
        lastActiveAt: now - DAY,
        authMethod: "email_code",
        device: "Windows PC",
        browser: "Edge",
        city: "Mumbai",
        ip: "103.21.58.140",
      },
    ],
    signIns: [],
    magicCodes: [],
    codeSends: {},
    authenticated: [],
    outbox: [
      inviteEmail(
        "ishaan.guest@example.in",
        "Rohan Mehta",
        "a guest",
        DEMO_TOKENS.guestInvitation,
        9,
      ),
      inviteEmail("tara.menon@example.in", "Asha Rao", "a member", DEMO_TOKENS.memberInvitation, 2),
    ],
    shareFailures: {},
  };
}
